import React, { useCallback, useEffect, useState } from "react";
import { MessageBar, MessageBarType, Text } from "@fluentui/react";
import {
    deleteDocument as apiDeleteDocument,
    listDocuments,
    listRagStores,
    moveDocument,
    updateDocumentNote,
    UploadJobProgress,
    uploadToRagStore
} from "../../../services/ataService";
import { Document, RagStore, UserPermissions } from "../../ata/types";
import AtaDocumentList from "./AtaDocumentList";
import AtaRagStoreList from "./AtaRagStoreList";
import styles from "./AtaManagementView.module.css";

interface AtaManagementViewProps {
    permissions: UserPermissions | null;
    token?: string;
    onError?: (msg: string) => void;
    onDocumentsChanged?: () => void;
}

const AtaManagementView: React.FC<AtaManagementViewProps> = ({ permissions, token, onError, onDocumentsChanged }) => {
    const [stores, setStores] = useState<RagStore[]>([]);
    const [selectedStore, setSelectedStore] = useState<RagStore | null>(null);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loadingStores, setLoadingStores] = useState(false);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [processingFile, setProcessingFile] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<UploadJobProgress | null>(null);
    const [backgroundJobInfo, setBackgroundJobInfo] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const isDev = !!permissions?.isDev;

    const notify = useCallback(
        (msg: string) => {
            setError(msg);
            onError?.(msg);
        },
        [onError]
    );

    const loadStores = useCallback(
        async (refreshSelectedStore?: RagStore | null) => {
            setLoadingStores(true);
            setError(null);
            try {
                let all = await listRagStores(token);
                // Filter by categories_google unless dev
                if (!isDev && permissions?.categories_google?.length) {
                    const allowed = permissions.categories_google.map(c => c.toLowerCase());
                    all = all.filter(s => allowed.includes(s.displayName.toLowerCase()) || (s.name && allowed.includes(s.name.toLowerCase())));
                }
                setStores(all);
                // If a store was selected, refresh its documents too
                const storeToRefresh = refreshSelectedStore !== undefined ? refreshSelectedStore : null;
                if (storeToRefresh) {
                    const updated = all.find(s => s.displayName === storeToRefresh.displayName) ?? storeToRefresh;
                    setSelectedStore(updated);
                    await loadDocuments(updated);
                }
            } catch (e: any) {
                notify("Impossible de charger les catégories : " + e.message);
            } finally {
                setLoadingStores(false);
            }
        },
        [token, isDev, permissions?.categories_google, notify]
    );

    useEffect(() => {
        loadStores();
    }, [loadStores]);

    const loadDocuments = useCallback(
        async (store: RagStore) => {
            if (!store.storeExists || !store.name) {
                // Store not yet created in Google — no documents to list
                setDocuments([]);
                return;
            }
            setLoadingDocs(true);
            setDocuments([]);
            try {
                const docs = await listDocuments(store.name, token, store.displayName);
                setDocuments(docs);
            } catch (e: any) {
                notify("Impossible de charger les documents : " + e.message);
            } finally {
                setLoadingDocs(false);
            }
        },
        [token, notify]
    );

    const handleSelectStore = (store: RagStore) => {
        setSelectedStore(store);
        loadDocuments(store);
    };

    const handleUpload = async (file: File) => {
        if (!selectedStore) return;
        setProcessingFile(file.name);
        setUploadProgress({ status: "processing", stage: "starting", progressPct: 0, pagesDone: 0, totalPages: null, filename: file.name });
        setBackgroundJobInfo(null);
        try {
            const result = await uploadToRagStore(
                selectedStore.displayName,
                file,
                token,
                () => setBackgroundJobInfo(file.name),
                progress => setUploadProgress(progress)
            );
            let nextSelectedStore = selectedStore;
            // If the store was just auto-created, update it in the stores list
            if (!selectedStore.storeExists || !selectedStore.name) {
                const updatedStore: RagStore = {
                    ...selectedStore,
                    name: result.storeName,
                    storeExists: true
                };
                setStores(prev => prev.map(s => (s.displayName === selectedStore.displayName ? updatedStore : s)));
                setSelectedStore(updatedStore);
                nextSelectedStore = updatedStore;
            }
            await loadDocuments(nextSelectedStore);
            onDocumentsChanged?.();
        } catch (e: any) {
            notify("Erreur lors de l'envoi : " + e.message);
        } finally {
            setProcessingFile(null);
            setUploadProgress(null);
            setBackgroundJobInfo(null);
        }
    };

    const handleDeleteDocument = async (doc: Document) => {
        if (!selectedStore || !selectedStore.name) return;
        try {
            const relatedNames = doc.relatedDocumentNames && doc.relatedDocumentNames.length > 0 ? doc.relatedDocumentNames : [doc.name];
            const [firstName, ...restNames] = relatedNames;
            if (firstName) {
                await apiDeleteDocument(selectedStore.name, firstName, token, doc.blobPath);
            }
            if (restNames.length > 0) {
                await Promise.all(restNames.map(name => apiDeleteDocument(selectedStore.name!, name, token, undefined)));
            }
            await loadDocuments(selectedStore);
            onDocumentsChanged?.();
        } catch (e: any) {
            notify("Erreur lors de la suppression du document : " + e.message);
        }
    };

    const handleSaveDocumentNote = async (doc: Document, note: string) => {
        if (!selectedStore || !selectedStore.name) return;
        try {
            await updateDocumentNote(selectedStore.name, doc.displayName, note, token);
            await loadDocuments(selectedStore);
            onDocumentsChanged?.();
        } catch (e: any) {
            notify("Erreur lors de la sauvegarde de la note : " + e.message);
            throw e;
        }
    };

    const handleMoveDocument = async (doc: Document, targetStoreDisplayName: string) => {
        if (!selectedStore || !selectedStore.name) return;
        try {
            await moveDocument(
                {
                    sourceStoreName: selectedStore.name,
                    sourceStoreDisplayName: selectedStore.displayName,
                    targetStoreDisplayName,
                    document: doc
                },
                token
            );
            await loadStores(selectedStore);
            await loadDocuments(selectedStore);
            onDocumentsChanged?.();
        } catch (e: any) {
            notify("Erreur lors du déplacement du document : " + e.message);
            throw e;
        }
    };

    return (
        <div className={styles.root}>
            {error && (
                <MessageBar messageBarType={MessageBarType.error} isMultiline={false} onDismiss={() => setError(null)} className={styles.errorBar}>
                    {error}
                </MessageBar>
            )}
            {backgroundJobInfo && (
                <MessageBar messageBarType={MessageBarType.info} isMultiline className={styles.errorBar}>
                    <strong>{backgroundJobInfo}</strong> a été envoyé au serveur et est en cours de traitement (extraction OCR). Vous pouvez quitter cette
                    fenêtre — le document sera disponible dans la base documentaire d'ici quelques instants.
                    {uploadProgress?.progressPct !== undefined && (
                        <>
                            {" "}
                            Progression actuelle: <strong>{uploadProgress.progressPct}%</strong>
                            {typeof uploadProgress.pagesDone === "number" && typeof uploadProgress.totalPages === "number"
                                ? ` (${uploadProgress.pagesDone}/${uploadProgress.totalPages} pages)`
                                : ""}
                            .
                        </>
                    )}
                </MessageBar>
            )}
            <div className={styles.body}>
                <div className={styles.sidebar}>
                    <AtaRagStoreList
                        stores={stores}
                        selectedStore={selectedStore}
                        isLoading={loadingStores}
                        onSelect={handleSelectStore}
                        onRefresh={() => loadStores(selectedStore)}
                    />
                </div>
                <div className={styles.main}>
                    <AtaDocumentList
                        selectedStore={selectedStore}
                        availableStores={stores}
                        documents={documents}
                        isLoading={loadingDocs}
                        processingFile={processingFile}
                        uploadProgress={uploadProgress}
                        isDev={isDev}
                        onUpload={handleUpload}
                        onDelete={handleDeleteDocument}
                        onSaveNote={handleSaveDocumentNote}
                        onMove={handleMoveDocument}
                    />
                </div>
            </div>
        </div>
    );
};

export default AtaManagementView;
